import { ArrowUp } from 'lucide-react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { UnconventionalConfigDetection } from '@/features/Quote/services/coverageRules';
import { cn } from '@/lib/utils';

interface CoverageWarningBannerProps {
	/** Detection result from coverageRules */
	detection: UnconventionalConfigDetection;
	/** Callback when user clicks adjust coverage button */
	onAdjustCoverage?: () => void;
	/** Whether the quotes list is currently polling/loading */
	isPolling?: boolean;
}

/**
 * Warning banner displayed when unconventional coverage configuration is detected
 *
 * Shows a general warning message with a button to adjust coverage.
 * Specific validation errors are displayed inline beneath each affected field.
 */
const CoverageWarningBanner = ({
	detection,
	onAdjustCoverage,
	isPolling = false
}: CoverageWarningBannerProps): React.JSX.Element | null => {
	const { t } = useTranslation();

	if (!detection.hasIssues) {
		return null;
	}

	return (
		<div
			className={cn(
				'flex p-0 items-start bg-primary/10 border border-primary/50 rounded-lg',
				isPolling && 'pointer-events-none saturate-[0.25] opacity-50'
			)}
			role="alert"
			data-testid="coverage-warning-banner"
		>
			<div className="grow bg-white/50 p-4 rounded-lg">
				<div className="flex flex-col md:flex-row items-center gap-3">
					<div className="grow text-center md:text-start">
						<h6 className="text-base font-bold text-primary mb-2">
							{t('quotes.coverageWarning.title')}
						</h6>
						<p className="text-foreground mb-0">{t('quotes.coverageWarning.description')}</p>
					</div>
					{onAdjustCoverage && (
						<Button
							type="button"
							variant="default"
							className="px-3 py-2 font-bold whitespace-nowrap"
							onClick={onAdjustCoverage}
							data-testid="coverage-adjust-button"
							iconRight={<ArrowUp className="h-4 w-4" />}
						>
							{t('quotes.coverageWarning.adjustButton')}
						</Button>
					)}
				</div>
			</div>
		</div>
	);
};

export default CoverageWarningBanner;
